import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Header from './Header';
import Sidebar from './Sidebar';
import Settings from './Settings';
import Assets from './Assets';
import Tickets from './Tickets';
import Software from './Software';
import Contacts from './Contacts';
import SelfService from './SelfService';
import Documents from './Documents';
import Orders from './Orders';
import Utilization from './Utilization';
import TenantUsers from './TenantUsers';
import UserManagement from './UserManagement';
import Analytics from './Analytics';
import CisBenchmarks from './CisBenchmarks';
import Overview from './Overview';
import Reports from './Reports';
import LicenseGrid from './LicenseGrid';
import Login from './Login';
import DevLogin from './DevLogin';
import Icon from './Icon';
import ComplianceGauge from './ComplianceGauge';
import './App.css';

function AppContent() {
  // Check if we're on the devlogin route FIRST, before any AuthContext checks
  const isDevLoginRoute = typeof window !== 'undefined' &&
    window.location &&
    window.location.pathname &&
    window.location.pathname.toLowerCase() === '/devlogin';

  const [currentPage, setCurrentPage] = useState('dashboard');
  const [navigationState, setNavigationState] = useState({});
  const [dashboardStats, setDashboardStats] = useState({ 
    assetCount: 0, 
    activeAssets: 0, 
    darkDevices: 0,
    ticketCount: 0, 
    satisfactionCounts: { good: 0, bad: 0 }, 
    satisfactionTotal: 0 
  });
  const [selfServiceKey, setSelfServiceKey] = useState(0);
  const [intuneCompliance, setIntuneCompliance] = useState({
    loading: false,
    configured: false,
    activeTotal: 0,
    conditionalAccess: { compliant: 0, nonCompliant: 0 },
    encryption: { compliant: 0, nonCompliant: 0 },
    antiMalware: { compliant: 0, nonCompliant: 0 }
  });
  const { isAuthenticated, loading, setOnTenantChange, selectedTenant, authenticatedFetch, user, hasAllTenantAccess, hasPrivilege } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const isFinance = (user?.role || '').toLowerCase() === 'finance';
  const canViewOverview = isAdmin && hasAllTenantAccess;
  const canViewReports = isFinance;
  useEffect(() => {
    const openHandler = (e) => {
      const { sku } = e.detail || {};
      setCurrentPage('licenseGrid');
      setNavigationState({ sku });
    };
    const navHandler = (e) => {
      const { sku } = e.detail || {};
      setCurrentPage('licenseGrid');
      setNavigationState({ sku });
    };
    const assetsNavHandler = (e) => {
      const { upn, initialFilters } = e.detail || {};
      if (initialFilters && typeof initialFilters === 'object' && Object.keys(initialFilters).length > 0) {
        setCurrentPage('assets');
        setNavigationState({ initialFilters });
        return;
      }
      if (upn) {
        setCurrentPage('assets');
        setNavigationState({ 
          initialFilters: { upn: upn.toLowerCase() },
          searchUpn: upn.toLowerCase()
        });
      }
    };
    window.addEventListener('navigateToLicenseGrid', navHandler);
    window.addEventListener('openLicenseGrid', openHandler);
    window.addEventListener('navigateToAssets', assetsNavHandler);
    return () => {
      window.removeEventListener('navigateToLicenseGrid', navHandler);
      window.removeEventListener('openLicenseGrid', openHandler);
      window.removeEventListener('navigateToAssets', assetsNavHandler);
    };
  }, []);

  useEffect(() => {
    const goAnalytics = () => {
      setCurrentPage('analytics');
      setNavigationState({});
    };
    window.addEventListener('navigateToAnalytics', goAnalytics);
    return () => window.removeEventListener('navigateToAnalytics', goAnalytics);
  }, []);

  useEffect(() => {
    const clearLicenseSku = () => {
      setNavigationState((prev) => {
        if (!prev || prev.sku == null) {
          return prev;
        }
        const next = { ...prev };
        delete next.sku;
        return next;
      });
    };
    window.addEventListener('clearLicenseGridNavigationSku', clearLicenseSku);
    return () => window.removeEventListener('clearLicenseGridNavigationSku', clearLicenseSku);
  }, []);

  const handleSettingsClick = () => {
    setCurrentPage('settings');
  };

  const handleBackToDashboard = () => {
    setCurrentPage('dashboard');
  };

  const handleMenuClick = (page, filters = {}) => {
    if (page === 'overview' && !canViewOverview) {
      return;
    }
    if (page === 'reports' && !canViewReports) {
      return;
    }
    
    // Check view privileges for each page
    const privilegeMap = {
      'dashboard': 'dashboard',
      'assets': 'assets',
      'software': 'software',
      'tickets': 'tickets',
      'selfservice': 'selfservice',
      'analytics': 'analytics',
      'documents': 'documents',
      'orders': 'orders',
      'cisBenchmarks': 'cisbenchmarks',
      'utilization': 'utilization',
      'settings': 'settings',
      'userManagement': 'settings'
    };
    
    const requiredPrivilege = privilegeMap[page];
    if (requiredPrivilege && !hasPrivilege(requiredPrivilege)) {
      // Redirect to dashboard if user doesn't have access
      setCurrentPage('dashboard');
      setNavigationState({});
      return;
    }
    
    setCurrentPage(page);
    setNavigationState(filters);
    
    // Force SelfService component to remount when navigating to it
    if (page === 'selfservice') {
      setSelfServiceKey(prev => prev + 1);
    }
  };

  const handleAssetClick = (serialNumber) => {
    setCurrentPage('software');
    setNavigationState({ 
      initialFilters: { serial: serialNumber.toLowerCase() },
      searchSerial: serialNumber.toLowerCase()
    });
  };

  const handleComplianceGaugeClick = (complianceType) => {
    if (!hasPrivilege('assets')) return;
    handleMenuClick('assets', { intuneComplianceFilter: complianceType });
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = useCallback(async () => {
    if (!selectedTenant) {
      setDashboardStats({ 
        assetCount: 0, 
        activeAssets: 0, 
        darkDevices: 0,
        ticketCount: 0, 
        satisfactionCounts: { good: 0, bad: 0 }, 
        satisfactionTotal: 0 
      });
      return;
    }

    try {
      // Fetch assets count
      const assetsResponse = await authenticatedFetch(`${getApiBaseUrl()}/api/assets?tenant=${selectedTenant.name}`);
      
      // Fetch tickets count
      const ticketsResponse = await authenticatedFetch(`${getApiBaseUrl()}/api/tickets?tenant=${selectedTenant.name}`);

      // Fetch satisfaction
      const satisfactionResponse = await authenticatedFetch(`${getApiBaseUrl()}/api/satisfaction?tenant=${selectedTenant.name}`);

      const assetsData = await assetsResponse.json();
      const ticketsData = await ticketsResponse.json();

      const satisfactionData = await satisfactionResponse.json();

      const openHoldCount = (ticketsData.success && Array.isArray(ticketsData.tickets))
        ? ticketsData.tickets.filter(t => {
            const status = (t.status || t.Status || '').toLowerCase();
            return status === 'open' || status === 'hold' || status === 'on-hold';
          }).length
        : 0;

      setDashboardStats({
        assetCount: assetsData.success ? assetsData.assets.length : 0,
        activeAssets: assetsData.success && assetsData.counts ? assetsData.counts.active : 0,
        darkDevices: assetsData.success && assetsData.counts ? assetsData.counts.dark : 0,
        ticketCount: openHoldCount,
        satisfactionCounts: satisfactionData.success ? satisfactionData.counts : { offered: 0, good: 0, bad: 0 },
        satisfactionTotal: satisfactionData.success ? satisfactionData.total : 0
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setDashboardStats({ 
        assetCount: 0, 
        activeAssets: 0, 
        darkDevices: 0,
        ticketCount: 0, 
        satisfactionCounts: { good: 0, bad: 0 }, 
        satisfactionTotal: 0 
      });
    }
  }, [selectedTenant, authenticatedFetch]);

  const fetchIntuneCompliance = useCallback(async () => {
    if (!selectedTenant) {
      setIntuneCompliance({
        loading: false,
        configured: false,
        activeTotal: 0,
        conditionalAccess: { compliant: 0, nonCompliant: 0 },
        encryption: { compliant: 0, nonCompliant: 0 },
        antiMalware: { compliant: 0, nonCompliant: 0 }
      });
      return;
    }

    setIntuneCompliance((prev) => ({ ...prev, loading: true }));
    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/dashboard/intune-compliance?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to load Intune compliance');
      }
      setIntuneCompliance({
        loading: false,
        configured: Boolean(data.configured),
        activeTotal: data.activeTotal || 0,
        conditionalAccess: data.conditionalAccess || { compliant: 0, nonCompliant: 0 },
        encryption: data.encryption || { compliant: 0, nonCompliant: 0 },
        antiMalware: data.antiMalware || { compliant: 0, nonCompliant: 0 }
      });
    } catch (error) {
      console.error('Error fetching Intune compliance:', error);
      setIntuneCompliance({
        loading: false,
        configured: false,
        activeTotal: 0,
        conditionalAccess: { compliant: 0, nonCompliant: 0 },
        encryption: { compliant: 0, nonCompliant: 0 },
        antiMalware: { compliant: 0, nonCompliant: 0 }
      });
    }
  }, [selectedTenant, authenticatedFetch]);

  // Fetch stats when tenant changes
  useEffect(() => {
    fetchDashboardStats();
    fetchIntuneCompliance();
  }, [fetchDashboardStats, fetchIntuneCompliance]);

  // Clear navigation state when tenant changes
  useEffect(() => {
    setOnTenantChange((newTenant) => {
      setNavigationState({});
      setCurrentPage('dashboard');
    });
  }, [setOnTenantChange]);

  useEffect(() => {
    if (!canViewOverview && currentPage === 'overview') {
      setCurrentPage('dashboard');
    }
  }, [canViewOverview, currentPage]);

  useEffect(() => {
    if (!canViewReports && currentPage === 'reports') {
      setCurrentPage('dashboard');
    }
  }, [canViewReports, currentPage]);

  // If on devlogin route, show DevLogin immediately (even during loading)
  if (isDevLoginRoute) {
    // Show DevLogin if not authenticated (even if still loading)
    if (!isAuthenticated) {
      return <DevLogin />;
    }
    // If authenticated and on devlogin route, redirect to dashboard
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', '/');
      setCurrentPage('dashboard');
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const renderContent = () => {
    // Check view privileges before rendering pages
    const privilegeMap = {
      'dashboard': 'dashboard',
      'assets': 'assets',
      'software': 'software',
      'tickets': 'tickets',
      'selfservice': 'selfservice',
      'analytics': 'analytics',
      'documents': 'documents',
      'orders': 'orders',
      'cisBenchmarks': 'cisbenchmarks',
      'utilization': 'utilization',
      'settings': 'settings',
      'userManagement': 'settings'
    };
    
    const requiredPrivilege = privilegeMap[currentPage];
    if (requiredPrivilege && !hasPrivilege(requiredPrivilege)) {
      // Redirect to dashboard if user doesn't have access
      return (
        <main className="main-content">
          <div className="welcome-section">
            <h1 className="welcome-title">Access Denied</h1>
            <p className="welcome-subtitle">You don't have permission to access this page.</p>
          </div>
        </main>
      );
    }
    
    switch (currentPage) {
      case 'dashboard':
        return (
          <main className="main-content">
            <div className="welcome-section">
              <h1 className="welcome-title">Welcome back!</h1>
              <p className="welcome-subtitle">Here's your customer portal overview</p>
            </div>
            
            <div className="stats-grid">
              {hasPrivilege('assets') && (
              <div className="stat-card clickable-stat" onClick={() => handleMenuClick('assets')}>
                <Icon name="totalAssets" size="medium" className="stat-icon" />
                <div className="stat-number">{dashboardStats.assetCount}</div>
                <div className="stat-label">Total Assets</div>
                <div className="stat-subcounts">
                  <span className="stat-subcount active">Active: {dashboardStats.activeAssets}</span>
                  <span className="stat-subcount dark">Dark: {dashboardStats.darkDevices}</span>
                </div>
              </div>
              )}
              {hasPrivilege('tickets') && (
              <div className="stat-card clickable-stat" onClick={() => handleMenuClick('tickets')}>
                <Icon name="tickets" size="medium" className="stat-icon" />
                <div className="stat-number">{dashboardStats.ticketCount}</div>
                <div className="stat-label">Tickets being handled</div>
              </div>
              )}
              {hasPrivilege('tickets') && (
              <div className="stat-card clickable-stat" onClick={() => handleMenuClick('tickets', {
                initialFilters: {},
                statusWhitelist: ['solved', 'closed'],
                createdSinceDays: 90,
                hideClosedSolved: false,
                excludeOffered: true
              })}>
                <Icon name="tickets" size="medium" className="stat-icon" />
                <div className="stat-number">
                  {dashboardStats.satisfactionTotal > 0
                    ? `${Math.round((dashboardStats.satisfactionCounts.good / dashboardStats.satisfactionTotal) * 100)}% Good`
                    : '—'}
                </div>
                <div className="stat-label">Satisfaction Ratings (last 3 months)</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, fontSize: 12 }}>
                  <span title="Good">Good: {dashboardStats.satisfactionCounts.good}</span>
                  <span title="Bad">Bad: {dashboardStats.satisfactionCounts.bad}</span>
                </div>
              </div>
              )}
            </div>

            {hasPrivilege('assets') && intuneCompliance.configured && (
            <div className="stats-grid intune-gauges-grid">
              <ComplianceGauge
                title="Conditional Access"
                compliant={intuneCompliance.conditionalAccess.compliant}
                nonCompliant={intuneCompliance.conditionalAccess.nonCompliant}
                total={intuneCompliance.activeTotal}
                onClick={() => handleComplianceGaugeClick('conditionalAccess')}
              />
              <ComplianceGauge
                title="Encryption"
                compliant={intuneCompliance.encryption.compliant}
                nonCompliant={intuneCompliance.encryption.nonCompliant}
                total={intuneCompliance.activeTotal}
                onClick={() => handleComplianceGaugeClick('encryption')}
              />
              <ComplianceGauge
                title="Anti-Malware"
                compliant={intuneCompliance.antiMalware.compliant}
                nonCompliant={intuneCompliance.antiMalware.nonCompliant}
                total={intuneCompliance.activeTotal}
                onClick={() => handleComplianceGaugeClick('antiMalware')}
              />
            </div>
            )}

            <div className="content-grid">
              {hasPrivilege('software') && (
              <div className="content-card" onClick={() => handleMenuClick('software')}>
                <div className="content-card-header">
                  <Icon name="software" size="medium" className="content-card-icon" />
                  <h3>Software Licenses</h3>
                </div>
                <p>Access and manage software licenses and subscriptions</p>
              </div>
              )}
              {hasPrivilege('tickets') && (
              <div className="content-card" onClick={() => handleMenuClick('tickets')}>
                <div className="content-card-header">
                  <Icon name="tickets" size="medium" className="content-card-icon" />
                  <h3>Support Tickets</h3>
                </div>
                <p>Track and manage support tickets and requests</p>
              </div>
              )}
              {hasPrivilege('analytics') && (
              <div className="content-card" onClick={() => handleMenuClick('analytics')}>
                <div className="content-card-header">
                  <Icon name="analytics" size="medium" className="content-card-icon" />
                  <h3>Analytics</h3>
                </div>
                <p>View detailed analytics and reporting</p>
              </div>
              )}
            </div>
          </main>
        );
      case 'assets':
        return (
          <main className="main-content">
            <Assets
              onAssetClick={handleAssetClick}
              initialFilters={navigationState.initialFilters}
              intuneComplianceFilter={navigationState.intuneComplianceFilter}
            />
          </main>
        );
      case 'tickets':
        return (
          <main className="main-content">
            <Tickets 
              initialFilters={navigationState.initialFilters}
              statusWhitelist={navigationState.statusWhitelist}
              createdSinceDays={navigationState.createdSinceDays}
              hideClosedSolved={navigationState.hideClosedSolved}
              excludeOffered={navigationState.excludeOffered}
            />
          </main>
        );
      case 'software':
        return (
          <main className="main-content">
            <Software 
              initialFilters={navigationState.initialFilters} 
              searchSerial={navigationState.searchSerial}
            />
          </main>
        );
      case 'contacts':
        return (
          <main className="main-content">
            <Contacts />
          </main>
        );
      case 'selfservice':
        return (
          <main className="main-content">
            <SelfService key={selfServiceKey} />
          </main>
        );
      case 'analytics':
        return (
          <main className="main-content">
            <Analytics />
          </main>
        );
      case 'cisBenchmarks':
        return (
          <main className="main-content">
            <CisBenchmarks />
          </main>
        );
      case 'documents':
        return (
          <main className="main-content">
            <Documents />
          </main>
        );
      case 'orders':
        return (
          <main className="main-content">
            <Orders />
          </main>
        );
      case 'utilization':
        return (
          <main className="main-content">
            <Utilization />
          </main>
        );
      case 'tenantUsers':
        return (
          <main className="main-content">
            <TenantUsers />
          </main>
        );
      case 'userManagement':
        if (!isAdmin) {
          return (
            <main className="main-content">
              <div className="welcome-section">
                <h1 className="welcome-title">Access Denied</h1>
                <p className="welcome-subtitle">Only administrators can access User Management.</p>
              </div>
            </main>
          );
        }
        return (
          <main className="main-content">
            <UserManagement />
          </main>
        );
      case 'overview':
        return (
          <main className="main-content">
            <Overview />
          </main>
        );
      case 'reports':
        if (!canViewReports) {
          return (
            <main className="main-content">
              <div className="welcome-section">
                <h1 className="welcome-title">Access Denied</h1>
                <p className="welcome-subtitle">Only finance users can access Reports.</p>
              </div>
            </main>
          );
        }
        return (
          <main className="main-content">
            <Reports />
          </main>
        );
      case 'licenseGrid':
        return (
          <main className="main-content">
            <LicenseGrid skuFilter={navigationState.sku} />
          </main>
        );
      case 'settings':
        return <Settings onBack={handleBackToDashboard} />;
      default:
        return (
          <main className="main-content">
            <h2>Welcome to Customer Portal</h2>
            <p>This is the main content area of your application.</p>
          </main>
        );
    }
  };

  return (
    <div className="App">
      <Header onSettingsClick={handleSettingsClick} onMenuClick={handleMenuClick} />
      <div className="app-body">
        <Sidebar onMenuClick={handleMenuClick} />
        {renderContent()}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
